import {
  useState, useEffect, useCallback, useRef, MutableRefObject, MouseEvent, FocusEvent, ChangeEvent, UIEvent,
} from 'react';
import noop from 'lodash/noop';
import { CustomContentProps, DropListComposedProps } from '../DropList';

const BLUR_DELAY = 200;

interface UseDropListEvents<Value, OptionValue> {
  internalValue: Value | string;
  isOpened: boolean;
  shouldStayOpened: DropListComposedProps<Value, OptionValue>['shouldStayOpened'];
  setIsOpened: (isOpened: boolean) => void;
  handleValueChange: (value: OptionValue | Value | string, event: ChangeEvent | { type: 'click' }, options?: {}) => void;
  targetRef: MutableRefObject<HTMLElement>;
  onTouched: (value: Value | string) => void;
  onBlur: (event: FocusEvent<any>) => void;
}

const useDropListEvents = <Value, OptionValue>({ internalValue, isOpened, setIsOpened, handleValueChange, targetRef, shouldStayOpened, onTouched, onBlur }: UseDropListEvents<Value, OptionValue>) => {
  const keepOpenedRef = useRef(false);
  const isBlurringRef = useRef(false);
  const isOpeningRef = useRef(false);
  const [isBlurring, setIsBlurring] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [blurringTime, setBlurringTime] = useState<number>();

  const updateIsBlurring = useCallback((newIsBlurring: boolean) => {
    isBlurringRef.current = newIsBlurring;
    setIsBlurring(newIsBlurring);
  }, []);

  const handleBlurEnd = useCallback(() => {
    setIsOpened(false);
    updateIsBlurring(false);
    setBlurringTime(0);
    if (onTouched) {
      onTouched(internalValue);
    }
  }, [internalValue, onTouched, setIsOpened, updateIsBlurring]);

  useEffect(() => {
    if (isBlurring) {
      const timeSinceBlurring = Date.now() - blurringTime;
      const delay = BLUR_DELAY - timeSinceBlurring;
      const timer = setTimeout(handleBlurEnd, delay);

      return () => {
        clearTimeout(timer);
      };
    }

    return noop;
  }, [isBlurring, internalValue, onTouched, setIsOpened, blurringTime, handleBlurEnd]);

  // On Safari, when selecting an item, it triggers the button for some reason
  useEffect(() => {
    if (isSelecting) {
      const timer = setTimeout(() => {
        setIsSelecting(false);
      }, 200);

      return () => {
        clearTimeout(timer);
      };
    }

    return noop;
  }, [isSelecting]);

  const keepOpen: CustomContentProps['keepOpen'] = useCallback((event: MouseEvent<any> | FocusEvent<any>, { focus = true } = {}) => {
    if (isBlurringRef.current) {
      updateIsBlurring(false);
      setBlurringTime(0);
    } else {
      keepOpenedRef.current = true;
    }

    setTimeout(() => {
      (event.nativeEvent as unknown as { keepDropListOpened: boolean }).keepDropListOpened = true;
      if (focus) targetRef.current?.focus();
    });
  }, [targetRef, updateIsBlurring]);

  const handleTargetClick = useCallback(() => {
    if (isBlurring) {
      updateIsBlurring(false);
      setBlurringTime(0);
    }

    if (!isSelecting) {
      const newIsOpened = isBlurring || !isOpened;
      setIsOpened(newIsOpened);

      if (newIsOpened) {
        isOpeningRef.current = true;

        setTimeout(() => {
          isOpeningRef.current = false;
        }, 200);
      }
    }
  }, [isBlurring, isSelecting, updateIsBlurring, setIsOpened, isOpened]);

  const handleItemClick = useCallback((newValue: OptionValue | string, event: MouseEvent) => {
    if (!(shouldStayOpened(newValue, event, {}))) {
      setIsOpened(false);
      setIsSelecting(true);
    } else {
      (event.nativeEvent as unknown as { keepDropListOpened: boolean }).keepDropListOpened = true;
      keepOpenedRef.current = true;
    }

    handleValueChange(newValue, { type: 'click' });

    setTimeout(() => {
      targetRef.current?.focus();
    }, 200);
  }, [handleValueChange, setIsOpened, shouldStayOpened, targetRef]);

  const handleTargetBlur: CustomContentProps['triggerBlur'] = useCallback((event: FocusEvent) => {
    if (isOpeningRef.current) return;

    if (!keepOpenedRef.current) {
      updateIsBlurring(true);
      setBlurringTime(Date.now());
    }

    keepOpenedRef.current = false;

    if (onBlur) onBlur(event);
  }, [onBlur, updateIsBlurring]);

  const handleDropListScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  return {
    keepOpen,
    handleTargetClick,
    handleItemClick,
    handleTargetBlur,
    handleDropListScroll,
    isBlurring,
  };
};

export default useDropListEvents;
